import { Button, Center, Group, Stack, Title } from "@mantine/core";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import logoCarburate from "@/assets/illustrations/logo-carburate.svg";
import walkthrough2 from "@/assets/walkthrough/2.png";
import walkthroughInspect from "@/assets/walkthrough/3.png";
import walkthroughMarket from "@/assets/walkthrough/4.png";
import walkthroughAuctions from "@/assets/walkthrough/5.png";
import { CarburateLogo, Carousel, TextSerif } from "@/components/elements";

type WalkthroughStep = {
  key: string;
  kind: "mark" | "image";
  src: string;
  title: string;
  description: string;
  cta: string;
};

export type WalkthroughProps = {
  onDone?: () => void;
  onStepChange?: (step: number) => void;
  compact?: boolean;
};

export const Walkthrough = ({ onDone, onStepChange, compact = false }: WalkthroughProps) => {
  const { t } = useTranslation();

  const steps = useMemo<WalkthroughStep[]>(
    () => [
      {
        key: "welcome",
        kind: "image",
        src: logoCarburate,
        title: t("components.walkthrough.steps.welcome.title"),
        description: t("components.walkthrough.steps.welcome.description"),
        cta: "",
      },
      {
        key: "garage",
        kind: "mark",
        src: walkthrough2,
        title: t("components.walkthrough.steps.garage.title"),
        description: t("components.walkthrough.steps.garage.description"),
        cta: "",
      },
      {
        key: "inspect",
        kind: "mark",
        src: walkthroughInspect,
        title: t("components.walkthrough.steps.inspect.title"),
        description: t("components.walkthrough.steps.inspect.description"),
        cta: "",
      },
      {
        key: "market",
        kind: "mark",
        src: walkthroughMarket,
        title: t("components.walkthrough.steps.market.title"),
        description: t("components.walkthrough.steps.market.description"),
        cta: "",
      },
      {
        key: "auctions",
        kind: "mark",
        src: walkthroughAuctions,
        title: t("components.walkthrough.steps.auctions.title"),
        description: t("components.walkthrough.steps.auctions.description"),
        cta: "",
      },
    ],
    [t]
  );

  const [active, setActive] = useState(0);
  const [embla, setEmbla] = useState<any>(null);

  const handleStepChange = (next: number) => {
    setActive(next);
    onStepChange?.(next);
  };

  const onCtaClick = () => {
    const isLast = active >= steps.length - 1;
    if (isLast) onDone?.();
    else embla?.scrollNext?.();
  };

  const TOP_LOGO_H = 64;
  const carouselHeight = compact ? 180 : 316;
  const activeStep = steps[active];
  const isLast = active >= steps.length - 1;
  const ctaLabel = isLast
    ? t("components.walkthrough.cta.startYourAdventure")
    : active === 0
      ? t("components.walkthrough.cta.startNow")
      : t("components.walkthrough.cta.continue");

  return (
    <Stack gap={0} px={0} pt={compact ? 8 : { base: 12, sm: 16 }} pb={{ base: 16, sm: 30 }}>
      {!compact && (
        <Center h={TOP_LOGO_H} pos="relative" bottom={20} style={{ width: "100%" }}>
          <CarburateLogo fill="#FFFFFF" w={210} />
        </Center>
      )}

      <Carousel
        initialSlide={0}
        height={carouselHeight}
        slideSize="100%"
        slideGap={0}
        withIndicators={false}
        withControls={false}
        emblaOptions={{ loop: false }}
        pb={0}
        onSlideChange={handleStepChange}
        getEmblaApi={setEmbla}
        styles={{
          viewport: { overflow: "hidden" },
        }}
      >
        {steps.map((step) => (
          <Carousel.Slide key={step.key} style={{ paddingInline: 6 }}>
            <Stack
              gap="md"
              align="center"
              justify={step.kind === "image" ? "flex-start" : "center"}
              style={{ height: "100%" }}
            >
              {step.kind === "image" && (
                <Center pt={compact ? 12 : 4}>
                  <img src={step.src} alt="Carburate" width={110} height={160} />
                </Center>
              )}

              {step.kind === "mark" && (
                <Center style={{ width: "100%", height: 270 }}>
                  <img
                    src={step.src}
                    alt={step.title}
                    style={{
                      width: "min(560px, 100%)",
                      maxWidth: "100%",
                      maxHeight: 250,
                      objectFit: "contain",
                      height: "auto",
                    }}
                  />
                </Center>
              )}
            </Stack>
          </Carousel.Slide>
        ))}
      </Carousel>

      {active > 0 && (
        <Group justify="center" gap={6} mt={12}>
          {steps.map((step, index) => (
            <Center
              key={step.key}
              component="button"
              type="button"
              aria-label={`Go to step ${index + 1}`}
              aria-current={index === active ? "step" : undefined}
              onClick={() => embla?.scrollTo?.(index)}
              style={{
                width: index === active ? 28 : 8,
                height: 8,
                border: "none",
                borderRadius: 999,
                padding: 0,
                cursor: "pointer",
                backgroundColor:
                  index === active
                    ? "var(--mantine-color-primary-6)"
                    : "var(--mantine-color-secondary-1)",
                transition: "width 250ms ease, background-color 250ms ease",
              }}
            />
          ))}
        </Group>
      )}

      <Center my={compact ? 16 : 12} w="100%">
        <Stack gap={compact ? 10 : 8} align="center" maw={440} w="100%" px="sm">
          <Title
            order={2}
            c="#FFFFFF"
            ta="center"
            fw={600}
            w="100%"
            style={{ letterSpacing: -0.2 }}
          >
            {activeStep.title}
          </Title>
          <TextSerif
            c="#FFFFFF"
            ta="center"
            fz="sm"
            w="100%"
            style={{ whiteSpace: "pre-line", lineHeight: 1.4, textWrap: "pretty" }}
          >
            {activeStep.description}
          </TextSerif>
        </Stack>
      </Center>

      <Group justify="center" gap="sm" wrap="nowrap" mt={compact ? 20 : 12}>
        <Button
          onClick={onCtaClick}
          w={250}
          h={44}
          radius="md"
          styles={{
            root: { backgroundColor: "var(--mantine-color-primary-6)" },
            label: { letterSpacing: 0.8, fontWeight: 600 },
          }}
        >
          {ctaLabel}
        </Button>
      </Group>
    </Stack>
  );
};
